import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { IMAGES, SITE } from "../../content/site";
import { MaskLines } from "../motion";
import { Cta } from "../Cta";
import { SwordMark } from "../Brand";

const EASE = [0.2, 0.7, 0.1, 1];

export function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#090909]" data-testid="home-hero">
      <motion.div
        className="absolute inset-x-0 top-0 h-[64%] md:inset-0 md:h-full"
        style={{ y }}
        initial={reduce ? false : { clipPath: "inset(18% 22% 18% 22%)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
        transition={{ duration: 1.6, ease: [0.7, 0, 0.2, 1] }}
      >
        <motion.picture
          className="block h-full w-full"
          initial={reduce ? false : { scale: 1.35 }}
          animate={{ scale: 1.06 }}
          transition={{ duration: 2.4, ease: EASE }}
        >
          <source media="(max-width: 767px)" srcSet={IMAGES.heroMobile} />
          <source media="(max-width: 1200px)" srcSet={IMAGES.heroSm} />
          <img
            src={IMAGES.hero}
            alt="A worshipper in a burgundy GNP hoodie raises both hands toward heaven in the sanctuary"
            className="h-full w-full object-cover object-[50%_30%] md:object-[50%_20%]"
            fetchpriority="high"
          />
        </motion.picture>
      </motion.div>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(9,9,9,0.55)_0%,rgba(9,9,9,0)_22%,rgba(9,9,9,0.35)_45%,#090909_66%)] md:bg-[linear-gradient(180deg,rgba(9,9,9,0.55)_0%,rgba(9,9,9,0.05)_30%,rgba(9,9,9,0.55)_62%,#090909_100%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_35%,transparent_30%,rgba(9,9,9,0.65)_100%)]" />

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-end px-5 pb-10 sm:px-8 sm:pb-14 lg:px-12">
        <motion.div
          className="mb-6 flex items-center gap-3"
          initial={reduce ? false : { opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
        >
          <SwordMark className="h-6 w-3 text-[#B8324E]" />
          <p className="font-display text-xs uppercase tracking-[0.32em] text-[#F6F2F0]/85">Youngstown, Ohio<span className="hidden sm:inline"> · Non-denominational</span></p>
        </motion.div>
        <MaskLines
          as="h1"
          onLoad
          delay={0.9}
          lines={[{ text: "God's Name", className: "text-[#F6F2F0]" }, { text: "Proclaimed.", className: "text-[#F6F2F0]" }]}
          className="font-display text-[17vw] font-bold uppercase leading-[0.84] tracking-[-0.02em] sm:text-[12vw] lg:text-[8.5rem] xl:text-[9.5rem] 2xl:text-[11rem]"
          testId="hero-headline"
        />
        <motion.div
          className="mt-8 grid gap-8 border-t border-[#F6F2F0]/15 pt-8 lg:grid-cols-12 lg:items-end"
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.9, ease: EASE }}
        >
          <p className="max-w-xl text-base leading-relaxed text-[#F6F2F0]/90 sm:text-lg lg:col-span-5" data-testid="hero-supporting-copy">
            A high-conviction, non-denominational Christian assembly in Youngstown, Ohio. Built to fear God, love God, and obey God.
          </p>
          <p className="font-display text-lg font-medium uppercase tracking-[0.22em] text-[#B8324E] sm:text-xl lg:col-span-3" data-testid="hero-motto">
            {SITE.motto.join(" ")}
          </p>
          <div className="flex flex-col gap-3 sm:flex-row lg:col-span-4 lg:flex-col lg:items-stretch xl:flex-row">
            <Cta to="/visit" testId="hero-join-sunday-button">Join Us Sunday at 2:30 PM</Cta>
            <Cta href={SITE.youtube} variant="ghost" testId="hero-youtube-button">Watch Messages on YouTube</Cta>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
